import {
  useAuthError,
  useIsAuthenticated,
  useLogin,
  useLogout,
  useRegister,
  useUser,
} from "#hooks/use-auth"
import { getUserFriendlyErrorMessage } from "#lib/errors"
import { User } from "@home-finance-manager/shared/types/users"
import React, { createContext, ReactNode, useCallback, useContext, useMemo } from "react"

/**
 * Auth context type with proper error handling
 */
interface AuthContextType {
  /** Current authenticated user or null if not authenticated */
  user: User | null
  /** Whether the user is authenticated */
  isAuthenticated: boolean
  /** Whether auth operations are in progress */
  isLoading: boolean
  /** Current error message, if any */
  error: string | null
  /** Clear the current error */
  clearError: () => void
  /** Login with email and password */
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>
  /** Logout the current user */
  logout: () => void
  /** Register a new user */
  register: (
    email: string,
    password: string,
    invitationToken?: string
  ) => Promise<{ success: boolean; error?: string }>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

/**
 * Hook to access auth context
 * @throws {Error} If used outside AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

/**
 * Auth provider component that manages authentication state
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { data: user, isLoading: userLoading } = useUser()
  const isAuthenticated = useIsAuthenticated()
  const loginMutation = useLogin()
  const registerMutation = useRegister()
  const logoutMutation = useLogout()

  // Get user-friendly error messages
  const loginError = useAuthError(loginMutation)
  const registerError = useAuthError(registerMutation)

  // Determine current error (prioritize active mutation errors)
  const error = useMemo(() => {
    if (loginMutation.isError && loginError) {
      return loginError
    }
    if (registerMutation.isError && registerError) {
      return registerError
    }
    return null
  }, [loginMutation.isError, loginError, registerMutation.isError, registerError])

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    // Errors are automatically cleared when mutations reset
    // This is mainly for manual error clearing if needed
    if (loginMutation.isError) {
      loginMutation.reset()
    }
    if (registerMutation.isError) {
      registerMutation.reset()
    }
  }, [loginMutation, registerMutation])

  /**
   * Login with email and password
   * Note: Input validation should be done at the form level using validators
   */
  const login = useCallback(
    async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
      try {
        // Clear any previous errors
        loginMutation.reset()

        // Attempt login (validation is handled by form validators and server)
        return await loginMutation
          .mutateAsync(
            { email, password },
            {
              onSuccess: () => {
                return { success: true }
              },
              onError: () => {
                return {
                  success: false,
                }
              },
            }
          )
          .then(() => {
            return { success: true }
          })
          .catch((error) => {
            return {
              success: false,
              error: getUserFriendlyErrorMessage(error),
            }
          })
      } catch (error) {
        const errorMessage = getUserFriendlyErrorMessage(error)
        return {
          success: false,
          error: errorMessage,
        }
      }
    },
    [loginMutation]
  )

  /**
   * Register a new user
   * Note: Input validation should be done at the form level using validators
   */
  const register = useCallback(
    async (
      email: string,
      password: string,
      invitationToken?: string
    ): Promise<{ success: boolean; error?: string }> => {
      try {
        // Clear any previous errors
        registerMutation.reset()

        // Attempt registration (validation is handled by form validators and server)
        return await registerMutation
          .mutateAsync(
            { email, password, invitationToken },
            {
              onSuccess: () => {
                return { success: true }
              },
              onError: () => {
                return { success: false }
              },
            }
          )
          .then(() => {
            return { success: true }
          })
          .catch(() => {
            return {
              success: false,
            }
          })
      } catch (error) {
        const errorMessage = getUserFriendlyErrorMessage(error)
        return {
          success: false,
          error: errorMessage,
        }
      }
    },
    [registerMutation]
  )

  /**
   * Logout the current user
   */
  const logout = useCallback(() => {
    try {
      logoutMutation.mutate()
    } catch (error) {
      // Log but don't throw - logout should always succeed
      console.error("Error during logout:", error)
    }
  }, [logoutMutation])

  const isLoading = useMemo(
    () => userLoading || loginMutation.isPending || registerMutation.isPending,
    [userLoading, loginMutation.isPending, registerMutation.isPending]
  )

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo<AuthContextType>(
    () => ({
      user: user ?? null,
      isAuthenticated,
      isLoading,
      error,
      clearError,
      login,
      logout,
      register,
    }),
    [user, isAuthenticated, isLoading, error, clearError, login, logout, register]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
